/* ---------------------------------------------------------------------------
   הנחה על כל הצעת המחיר — אותו חישוב כמו בשרת (src/modules/quotes/quote-discount.ts),
   לתצוגה בלבד. השרת מחשב ב-Decimal ומחליט.

   שורות → הנחה (אחוז מסכום השורות, או סכום קבוע) → מע"מ על הסכום שאחרי ההנחה →
   סה"כ. הכל באגורות שלמות; אחוז מעוגל חצי-למעלה לאגורה.
   --------------------------------------------------------------------------- */

export type DiscountType = 'PERCENT' | 'AMOUNT';
export type VatMode = 'added' | 'included' | 'exempt';

export interface DiscountDraft {
  type: DiscountType;
  /** כפי שהוקלד: "10", "12.5", "150.00". ריק = אין הנחה. */
  value: string;
  label: string;
}

export type DiscountIssue = 'invalid' | 'zero' | 'percentOver100' | 'overSubtotal';

/** כמו PRICE_PATTERN בשרת: לא שלילי, עד 2 ספרות אחרי הנקודה. */
const VALUE_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

/** "12.5" → 1250 (מאיות), בלי float. */
function hundredths(value: string): number {
  const [int = '0', frac = ''] = value.split('.');
  return Number(int) * 100 + Number(frac.padEnd(2, '0'));
}

/** הבעיה בהנחה, או null. הנחה ריקה אינה בעיה — פשוט אין הנחה. */
export function discountIssue(d: DiscountDraft, subtotalAgorot: number): DiscountIssue | null {
  const value = d.value.trim();
  if (value === '') return null;
  if (!VALUE_PATTERN.test(value)) return 'invalid';
  const h = hundredths(value);
  if (h === 0) return 'zero';
  if (d.type === 'PERCENT' && h > 100_00) return 'percentOver100';
  if (d.type === 'AMOUNT' && h > subtotalAgorot) return 'overSubtotal';
  return null;
}

/** ההנחה באגורות. הנחה ריקה או לא תקינה — 0. */
export function discountAgorot(d: DiscountDraft, subtotalAgorot: number): number {
  if (d.value.trim() === '' || discountIssue(d, subtotalAgorot)) return 0;
  const h = hundredths(d.value.trim());
  if (d.type === 'AMOUNT') return h;
  // subtotal × אחוז/100, מעוגל חצי למעלה — בשלמים (אחוז במאיות → /10,000).
  const n = subtotalAgorot * h;
  const q = Math.floor(n / 10_000);
  return n - q * 10_000 >= 5_000 ? q + 1 : q;
}

export interface QuoteTotals {
  subtotal: number;
  discount: number;
  afterDiscount: number;
  net: number;
  vat: number;
  gross: number;
}

/** כמו quoteTotals בשרת, אחרי ההנחה. באגורות. */
export function quoteTotals(
  subtotalAgorot: number,
  discount: number,
  mode: VatMode,
  rate: number,
): QuoteTotals {
  const after = subtotalAgorot - discount;
  if (mode === 'exempt')
    return {
      subtotal: subtotalAgorot,
      discount,
      afterDiscount: after,
      net: after,
      vat: 0,
      gross: after,
    };
  if (mode === 'included') {
    const net = Math.round(after / (1 + rate / 100));
    return {
      subtotal: subtotalAgorot,
      discount,
      afterDiscount: after,
      net,
      vat: after - net,
      gross: after,
    };
  }
  const vat = Math.round((after * rate) / 100);
  return {
    subtotal: subtotalAgorot,
    discount,
    afterDiscount: after,
    net: after,
    vat,
    gross: after + vat,
  };
}

/** הגוף לשרת: `discount` רק כשיש הנחה תקינה. */
export function discountBody(
  d: DiscountDraft,
): { type: DiscountType; value: string; label?: string } | undefined {
  const value = d.value.trim();
  if (value === '') return undefined;
  const label = d.label.trim();
  return { type: d.type, value, ...(label ? { label } : {}) };
}
